import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  { id: "1w", label: "1 settimana", price: 5 },
  { id: "2w", label: "2 settimane", price: 10 },
  { id: "4w", label: "4 settimane", price: 20 },
];

export const isFeaturedActive = (item) =>
  item?.is_featured ?? Boolean(item?.featured_until && new Date(item.featured_until) > new Date());

export const FeaturedBadge = ({ item, className = "" }) =>
  isFeaturedActive(item) ? (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[11px] font-bold uppercase tracking-wide ${className}`}
      data-testid={`featured-badge-${item.id}`}
    >
      <Star className="w-3 h-3 fill-current" /> In evidenza
    </span>
  ) : null;

export const FeaturedStats = ({ item }) => {
  if (!isFeaturedActive(item) || !item.featured_started_at) return null;
  const days = Math.max(1, (Date.now() - new Date(item.featured_started_at)) / 86400000);
  const during = Math.max(0, (item.views || 0) - (item.featured_views_start || 0));
  const dailyDuring = during / days;
  const pre = item.featured_pre_daily_views || 0;
  const extra = Math.max(0, Math.round(during - pre * days));
  const pct = pre > 0 ? Math.round(((dailyDuring - pre) / pre) * 100) : null;
  const daysLeft = Math.max(0, Math.ceil((new Date(item.featured_until) - Date.now()) / 86400000));

  return (
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs" data-testid={`featured-stats-${item.id}`}>
      <div><p className="text-amber-800">Visite in evidenza</p><p className="text-base font-bold text-slate-900" data-testid={`featured-stats-views-${item.id}`}>{during}</p></div>
      <div><p className="text-amber-800">Visite in più</p><p className="text-base font-bold text-emerald-700" data-testid={`featured-stats-extra-${item.id}`}>+{extra}</p></div>
      <div><p className="text-amber-800">Al giorno</p><p className="text-base font-bold text-slate-900">{dailyDuring.toFixed(1)} <span className="text-[11px] font-normal text-slate-500">(prima {pre.toFixed(1)})</span></p></div>
      <div><p className="text-amber-800">{pct !== null ? "Crescita" : "Giorni rimasti"}</p><p className="text-base font-bold text-slate-900" data-testid={`featured-stats-pct-${item.id}`}>{pct !== null ? `${pct >= 0 ? "+" : ""}${pct}%` : daysLeft}</p></div>
    </div>
  );
};

export const FeaturedButton = ({ kind, item }) => {
  const [open, setOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const active = isFeaturedActive(item);

  const checkout = async (packageId) => {
    setLoadingId(packageId);
    try {
      const res = await axios.post(`${API}/featured/checkout`, {
        kind, item_id: item.id, package_id: packageId, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il pagamento");
      setLoadingId(null);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-amber-300 text-amber-700 hover:bg-amber-50"
        data-testid={`featured-open-btn-${item.id}`}
      >
        <Star className="w-4 h-4 mr-1" /> {active ? "Prolunga evidenza" : "Metti in evidenza"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="featured-dialog">
          <DialogHeader>
            <DialogTitle>Metti in evidenza</DialogTitle>
            <DialogDescription>
              "{item.title}" apparirà in cima alla lista con l'etichetta "In evidenza".
              {active && ` Attualmente in evidenza fino al ${new Date(item.featured_until).toLocaleDateString("it-CH")}: il periodo verrà aggiunto.`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            {PACKAGES.map((p) => (
              <button
                key={p.id}
                onClick={() => checkout(p.id)}
                disabled={!!loadingId}
                className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors disabled:opacity-60"
                data-testid={`featured-package-${p.id}`}
              >
                <span className="font-medium text-slate-900">{p.label}</span>
                <span className="flex items-center gap-2 font-bold text-slate-900">
                  {loadingId === p.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  CHF {p.price}
                </span>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">Pagamento sicuro con carta tramite Stripe.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const useFeaturedReturn = (onPaid) => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("featured");
    if (!flag) return;
    const clean = () => window.history.replaceState({}, "", window.location.pathname);
    if (flag === "cancelled") {
      toast.info("Pagamento annullato");
      clean();
      return;
    }
    const sessionId = params.get("session_id");
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/featured/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          toast.success("Il tuo annuncio è ora in evidenza!");
          clean();
          onPaid?.();
          return;
        }
      } catch (e) { /* riprova */ }
      if (++attempts < 8) setTimeout(poll, 2000);
      else { toast.error("Pagamento non ancora confermato. Ricarica la pagina tra poco."); clean(); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
